import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import "./EditProject.css";
import "../common.css";
import { getUsersSummary, getAllDepartments } from "../../../api/api";
import {
  getProjectTypes,
  getAllBlocks,
  addBlock,
  getProjectById,
  getAllProjectsSummary,
  updateProject,
} from "../../../api/projectApi";
import { useNavigate } from "react-router-dom";
import Preloader from "../../Preloader/Preloader.js";
import Select from "react-select";
import Calendar from "../../Calendar/Calendar";
import CreateBlockModal from "../CreateBlockModal/CreateBlockModal";
import FinansistPayrollMapping from "../FinansistPayrollMapping/FinansistPayrollMapping";
import {
  buildFinansistPayrollMappingPayload,
  emptyFinansistMappingFormState,
  getProjectFinansistMapping,
  mappingToFormState,
} from "../../../utils/finansistPayrollMapping";

const EditProject = () => {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectData, setProjectData] = useState({
    code: "",
    projectView: "",
    type: null,
    buildingType: "",
    name: "",
    contractNumber: "",
    cipher: "",
    customer: "",
    signingDate: "",
    contractAmount: "",
    status: "",
    projectInfo: "",
    responsible: "",
    startDate: "",
    expertiseStartDate: "",
    expertiseEndDate: "",
    endDate: "",
    blocks: [],
    departmentId: "",
  });
  const [users, setUsers] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [showCreateBlockModal, setShowCreateBlockModal] = useState(false);
  const [createBlockSubmitting, setCreateBlockSubmitting] = useState(false);
  const [finansistMapping, setFinansistMapping] = useState(
    emptyFinansistMappingFormState,
  );

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [projectsData, usersData, projectTypesData, blocksData, departmentsData] =
          await Promise.all([
            getAllProjectsSummary(),
            getUsersSummary({ isActive: true, pageSize: 200 }),
            getProjectTypes(),
            getAllBlocks(),
            getAllDepartments(),
          ]);

        const projectOptions = projectsData.map((p) => ({
          value: p.id,
          label: p.name,
        }));
        setProjects(projectOptions);

        setUsers(
          usersData?.users?.map((user) => ({
            value: user.id,
            label: `${user.surname} ${user.name}`,
          })) || []
        );

        setProjectTypes(projectTypesData);

        const activeBlocks = blocksData
          // .filter((block) => block.is_active)
          .map((block) => ({
            value: block.id,
            label: block.name,
          }));
        setBlocks(activeBlocks);

        const activeDepartments = departmentsData
          .filter((dept) => dept.is_active)
          .map((dept) => ({
            value: dept.id,
            label: dept.name,
          }));
        setDepartments(activeDepartments);

        if (projectId) {
          const projectToEdit = projectOptions.find(
            (p) => p.value === parseInt(projectId)
          );
          if (projectToEdit) {
            handleProjectChange(projectToEdit);
          }
        }
      } catch (error) {
        console.error("Ошибка при загрузке данных:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [projectId]);

  // Дополнительная логика для загрузки проекта напрямую, если projectId передан
  useEffect(() => {
    if (
      projectId &&
      !selectedProject &&
      projectTypes.length > 0 &&
      users.length > 0
    ) {
      const loadProjectDirectly = async () => {
        try {
          setIsLoading(true);
          const projectData = await getProjectById(projectId);
          console.log("Полученные данные проекта:", projectData);
          console.log("Блоки проекта:", projectData.blocks);

          // Используем project_type из ответа, если он есть, иначе ищем в projectTypes
          let projectType = null;
          if (projectData.project_type) {
            // Если есть объект project_type в ответе, используем его
            projectType = projectData.project_type;
          } else if (projectData.project_type_id) {
            // Иначе ищем в загруженных типах проектов
            projectType = projectTypes.find(
              (type) => type.id === projectData.project_type_id
            ) || null;
          }

          const formattedProject = {
            code: projectData.cipher_pilot || "",
            projectView: projectData.project_category || "",
            type: projectType,
            buildingType: projectData.building_type || "",
            name: projectData.name || "",
            contractNumber: projectData.contract_number || "",
            cipher: projectData.cipher_fin || "",
            customer: projectData.customer || "",
            signingDate: projectData.contract_year || "",
            contractAmount: projectData.contract_amount
              ? projectData.contract_amount.toString()
              : "",
            status: projectData.status || "",
            projectInfo: projectData.information || "",
            responsible: projectData.chief_id || "",
            startDate: projectData.date_start || "",
            expertiseStartDate: projectData.expert_start || "",
            expertiseEndDate: projectData.expert_end || "",
            endDate: projectData.date_end || "",
            blocks: projectData.blocks
              .map((block) => ({
                value: block.id || `block-${block.name}`,
                label: block.name,
              }))
              .filter((block) => block.value && block.label),
            departmentId: projectData.department_id || "",
          };

          console.log("Отформатированные блоки:", formattedProject.blocks);
          setProjectData(formattedProject);
          setFinansistMapping(
            mappingToFormState(getProjectFinansistMapping(projectData)),
          );
          console.log("Текущее состояние projectData:", formattedProject);
        } catch (error) {
          console.error("Ошибка при загрузке данных проекта:", error);
        } finally {
          setIsLoading(false);
        }
      };

      loadProjectDirectly();
    }
  }, [projectId, projectTypes.length, users.length, selectedProject]);

  const handleProjectChange = async (selectedOption) => {
    setSelectedProject(selectedOption);
    setIsLoading(true);

    try {
      const projectData = await getProjectById(selectedOption.value);
      console.log("Полученные данные проекта:", projectData);
      console.log("Блоки проекта:", projectData.blocks);

      // Используем project_type из ответа, если он есть, иначе ищем в projectTypes
      let projectType = null;
      if (projectData.project_type) {
        // Если есть объект project_type в ответе, используем его
        projectType = projectData.project_type;
      } else if (projectData.project_type_id) {
        // Иначе ищем в загруженных типах проектов
        projectType = projectTypes.find(
          (type) => type.id === projectData.project_type_id
        ) || null;
      }

      const formattedProject = {
        code: projectData.cipher_pilot || "",
        projectView: projectData.project_category || "",
        type: projectType,
        buildingType: projectData.building_type || "",
        name: projectData.name || "",
        contractNumber: projectData.contract_number || "",
        cipher: projectData.cipher_fin || "",
        customer: projectData.customer || "",
        signingDate: projectData.contract_year || "",
        contractAmount: projectData.contract_amount
          ? projectData.contract_amount.toString()
          : "",
        status: projectData.status || "",
        projectInfo: projectData.information || "",
        responsible: projectData.chief_id || "",
        startDate: projectData.date_start || "",
        expertiseStartDate: projectData.expert_start || "",
            expertiseEndDate: projectData.expert_end || "",
            endDate: projectData.date_end || "",
            blocks: projectData.blocks
              .map((block) => ({
                value: block.id || `block-${block.name}`,
                label: block.name,
              }))
              .filter((block) => block.value && block.label),
            departmentId: projectData.department_id || "",
          };

      console.log("Отформатированные блоки:", formattedProject.blocks);
      setProjectData(formattedProject);
      setFinansistMapping(
        mappingToFormState(getProjectFinansistMapping(projectData)),
      );

      // Добавим лог после установки данных
      console.log("Текущее состояние projectData:", formattedProject);
    } catch (error) {
      console.error("Ошибка при загрузке данных проекта:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const validateField = (name, value) => {
    const requiredFields = [
      "code",
      "projectView",
      "type",
      "buildingType",
      "name",
      "contractNumber",
      "cipher",
      "customer",
      "signingDate",
      "contractAmount",
      "status",
      "projectInfo",
      "responsible",
      "startDate",
      "endDate",
      "departmentId",
    ];

    if (!requiredFields.includes(name)) {
      return "";
    }

    if (name === "type") {
      return value === null ? "Это поле обязательно для заполнения" : "";
    }

    if (name === "blocks") {
      return "";
    }

    if (!value || (typeof value === "string" && !value.trim())) {
      return "Это поле обязательно для заполнения";
    }

    return "";
  };

  const validateAllFields = () => {
    const newErrors = {};
    const requiredFields = [
      "code",
      "projectView",
      "type",
      "buildingType",
      "name",
      "contractNumber",
      "cipher",
      "customer",
      "signingDate",
      "contractAmount",
      "status",
      "projectInfo",
      "responsible",
      "startDate",
      "endDate",
      "departmentId",
      "blocks",
    ];

    requiredFields.forEach((field) => {
      const value = projectData[field];
      const error = validateField(field, value);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);
    setSubmitAttempted(true);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === "code") {
      // Разрешаем цифры, латиницу, кириллицу (вкл. Ё/ё), дефис, слэш, подчеркивание и запятую
      newValue = value.replace(/[^a-zA-Zа-яА-ЯёЁ0-9\-/_,]/g, "");
    } else if (name === "contractAmount") {
      newValue = value.replace(/\D/g, "");
    } else if (name === "projectView") {
      if (
        value === "" ||
        value.toUpperCase() === "Б" ||
        value.toUpperCase() === "К" ||
        /^[Кк]\d*$/.test(value)
      ) {
        newValue = value.toUpperCase();
      } else {
        return;
      }
    }

    setProjectData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    const error = validateField(name, newValue);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const handleTypeChange = (e) => {
    const selectedType = projectTypes.find(
      (type) => type.id === parseInt(e.target.value, 10)
    );
    setProjectData((prev) => ({
      ...prev,
      type: selectedType,
    }));

    setTouched((prev) => ({
      ...prev,
      type: true,
    }));

    setErrors((prev) => ({
      ...prev,
      type: validateField("type", selectedType),
    }));
  };

  const CREATE_BLOCK_OPTION = { value: "__create_block__", label: "+ Создать блок" };

  const handleBlockChange = async (selectedOptions) => {
    const options = selectedOptions || [];
    const createOpt = options.find((o) => o.value === CREATE_BLOCK_OPTION.value);
    if (createOpt) {
      setShowCreateBlockModal(true);
      return;
    }
    setProjectData((prev) => ({
      ...prev,
      blocks: options,
    }));
    if (submitAttempted || touched.blocks) {
      const error = validateField("blocks", options);
      setErrors((prev) => ({
        ...prev,
        blocks: error,
      }));
    }
  };

  const handleCreateBlock = async (nameNorm) => {
    setCreateBlockSubmitting(true);
    try {
      const newId = await addBlock(nameNorm, true);
      const blocksData = await getAllBlocks();
      const activeBlocks = blocksData.map((block) => ({
        value: block.id,
        label: block.name,
      }));
      setBlocks(activeBlocks);
      const newBlock = activeBlocks.find(
        (b) => String(b.value) === String(newId) || b.label === nameNorm
      );
      if (newBlock) {
        setProjectData((prev) => ({
          ...prev,
          blocks: [...prev.blocks, newBlock],
        }));
        if (submitAttempted || touched.blocks) {
          const error = validateField("blocks", [...projectData.blocks, newBlock]);
          setErrors((prev) => ({ ...prev, blocks: error }));
        }
      }
      setShowCreateBlockModal(false);
    } catch (err) {
      alert("Ошибка создания блока: " + (err.message || err));
    } finally {
      setCreateBlockSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Валидация всех полей
    if (!validateAllFields()) {
      alert("Пожалуйста, заполните все обязательные поля");
      return;
    }

    setIsLoading(true);

    try {
      // Функция для форматирования даты
      const formatDate = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        // Корректируем дату, чтобы избежать проблем с часовым поясом
        const adjustedDate = new Date(
          date.getTime() + date.getTimezoneOffset() * 60000
        );
        return adjustedDate.toISOString().split("T")[0];
      };

      const updatedProjectData = {
        name: projectData.name || "",
        cipher_pilot: projectData.code || "",
        cipher_fin: projectData.cipher || "",
        contract_amount: projectData.contractAmount
          ? Number(projectData.contractAmount)
          : 0,
        contract_number: projectData.contractNumber || "",
        building_type: projectData.buildingType || "",
        contract_year: formatDate(projectData.signingDate),
        project_category: projectData.projectView || "",
        status: projectData.status || "",
        date_start: formatDate(projectData.startDate),
        expert_start: formatDate(projectData.expertiseStartDate),
        expert_end: formatDate(projectData.expertiseEndDate),
        date_end: formatDate(projectData.endDate),
        information: projectData.projectInfo || "",
        customer: projectData.customer || "",
        chief_id: projectData.responsible
          ? Number(projectData.responsible)
          : null,
        project_type_id: projectData.type ? Number(projectData.type.id) : null,
        department_id: projectData.departmentId ? parseInt(projectData.departmentId, 10) : null,
        blocks: projectData.blocks
          .filter((block) => block && block.label)
          .map((block) => block.label),
      };

      const finansistPayload = buildFinansistPayrollMappingPayload({
        portalProjectName: projectData.name || "",
        finansistProject: finansistMapping.finansistProject,
      });
      if (finansistPayload) {
        updatedProjectData.finansist_payroll_mapping = finansistPayload;
      }

      // Удаляем null значения
      Object.keys(updatedProjectData).forEach((key) => {
        if (
          updatedProjectData[key] === null ||
          updatedProjectData[key] === ""
        ) {
          delete updatedProjectData[key];
        }
      });

      console.log("Отправляемые данные:", updatedProjectData);
      const projectIdToUpdate = projectId || selectedProject.value;
      await updateProject(projectIdToUpdate, updatedProjectData);

      setShowPopup(true);
      setTimeout(() => {
        setShowPopup(false);
        navigate("/projects/list");
      }, 3000);
    } catch (error) {
      console.error("Ошибка при обновлении проекта:", error);
      alert(`Ошибка при обновлении проекта: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const customStyles = (hasError) => ({
    control: (provided) => ({
      ...provided,
      background: "#F8F8F8",
      borderColor: hasError ? "#ff4d4f" : "#E0E0E0",
      borderRadius: "8px",
      padding: 0,
      fontFamily: "Manrope",
      fontSize: "14px",
      minHeight: "40px",
      height: "40px",
      boxShadow: "none",
      "&:hover": {
        borderColor: hasError ? "#ff4d4f" : "#2A5E38",
      },
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: "8px",
      marginTop: "4px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#2A5E38"
        : state.isFocused
        ? "#f5f5f5"
        : "white",
      color: state.isSelected ? "white" : "#333",
      padding: "8px 12px",
      fontFamily: "Manrope",
      fontSize: "14px",
      cursor: "pointer",
    }),
    singleValue: (provided) => ({
      ...provided,
      fontFamily: "Manrope",
      fontSize: "14px",
      color: "#333",
      margin: 0,
      padding: "0 8px",
      lineHeight: "40px",
    }),
    placeholder: (provided) => ({
      ...provided,
      fontFamily: "Manrope",
      fontSize: "14px",
      color: "#999",
      margin: 0,
      padding: "0 8px",
      lineHeight: "40px",
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0",
      height: "38px",
      display: "flex",
      alignItems: "center",
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
      padding: "0 8px",
      height: "38px",
      display: "flex",
      alignItems: "center",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "38px",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "0 8px",
      height: "38px",
      display: "flex",
      alignItems: "center",
    }),
  });

  const multiSelectStyles = (hasError) => ({
    ...customStyles(hasError),
    control: (provided, state) => ({
      ...provided,
      background: "#F8F8F8",
      borderColor: "#E0E0E0",
      borderRadius: "8px",
      padding: 0,
      fontFamily: "Manrope",
      fontSize: "14px",
      minHeight: "40px",
      height:
        state.hasValue && state.selectProps.value.length > 2 ? "auto" : "40px",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#2A5E38",
      },
    }),
    valueContainer: (provided, state) => ({
      ...provided,
      padding: "2px 8px",
      minHeight: "38px",
      height:
        state.hasValue && state.selectProps.value.length > 2 ? "auto" : "38px",
      display: "flex",
      alignItems: "flex-start",
      flexWrap: "wrap",
      gap: "2px",
      paddingTop: "2px",
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#E8E8F8",
      borderRadius: "4px",
      margin: "2px",
      height: "28px",
      display: "flex",
      alignItems: "center",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#333",
      padding: "2px 6px",
      fontSize: "14px",
      fontFamily: "Manrope",
      lineHeight: "24px",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      borderRadius: "0 4px 4px 0",
      padding: "0 4px",
      height: "28px",
      display: "flex",
      alignItems: "center",
      "&:hover": {
        backgroundColor: "#2A5E38",
        color: "white",
      },
    }),
    indicatorsContainer: (provided, state) => ({
      ...provided,
      height:
        state.hasValue && state.selectProps.value.length > 2 ? "100%" : "38px",
      alignItems: "center",
      alignSelf: "flex-start",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "0 8px",
      height: "38px",
      display: "flex",
      alignItems: "center",
      alignSelf: "flex-start",
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: "0 8px",
      height: "38px",
      display: "flex",
      alignItems: "center",
      alignSelf: "flex-start",
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
      padding: "2px 6px",
      height: "32px",
      display: "flex",
      alignItems: "center",
    }),
    placeholder: (provided) => ({
      ...provided,
      margin: 0,
      lineHeight: "38px",
    }),
  });

  const isFormValid = () => {
    const requiredFields = [
      "code",
      "projectView",
      "type",
      "buildingType",
      "name",
      "contractNumber",
      "cipher",
      "customer",
      "signingDate",
      "contractAmount",
      "status",
      "projectInfo",
      "responsible",
      "startDate",
      "endDate",
      "departmentId",
    ];

    return requiredFields.every((field) => {
      const value = projectData[field];
      if (field === "type") {
        return value !== null && (!errors[field] || errors[field] === "");
      }
      return value && value !== "" && (!errors[field] || errors[field] === "");
    });
  };

  if (isLoading) {
    return <Preloader />;
  }

  console.log("Доступные блоки:", blocks);
  console.log("Выбранные блоки проекта:", projectData.blocks);

  return (
    <div className="project-form-container">
      <form className="project-form" onSubmit={handleSubmit}>
        <ul className="editproject__points">
          <li className="editproject__point-select">
            <span className="editproject__point-title">
              Редактирование проекта
            </span>
            <Select
              options={projects}
              value={
                projectId
                  ? projects.find((p) => p.value === parseInt(projectId)) ||
                    null
                  : selectedProject
              }
              onChange={(opt) => {
                if (!opt) return;
                setSelectedProject(opt);
                navigate(`/projects/edit/${opt.value}`);
              }}
              placeholder="Начните вводить название или шифр проекта..."
              styles={customStyles(false)}
            />
          </li>
        </ul>
        <ul className="editproject__points">
          {false && (
            <li className="editproject__point editproject__point--full-width"></li>
          )}

          {/* {projectId && projectData && (
            <li className="editproject__point editproject__point--full-width">
              <span className="editproject__point-title">
                Редактирование проекта
              </span>
              <input
                value={projectData.name || "Загрузка..."}
                readOnly
                className="editproject__project-name-display"
                style={{
                  background: "#F8F8F8",
                  border: "1px solid #E0E0E0",
                  borderRadius: "8px",
                  padding: "10px 12px",
                  fontFamily: "Manrope",
                  fontSize: "14px",
                  color: "#333",
                  width: "100%",
                  cursor: "default",
                }}
              />
            </li>
          )} */}

          {((projectId && projectData) || (selectedProject && projectData)) && (
            <>
              <li className="editproject__point editproject__point--full-width">
                <span className="editproject__point-title">
                  Шифр проекта (Finansist)
                </span>
                <input
                  name="cipher"
                  value={projectData.cipher}
                  readOnly
                  placeholder="Шифр будет сгенерирован автоматически"
                />
              </li>
              <li className="editproject__point">
                <span className="editproject__point-title">Тип проекта</span>
                <select
                  name="type"
                  value={projectData.type ? projectData.type.id : ""}
                  onChange={handleTypeChange}
                  onBlur={() => {
                    setTouched((prev) => ({ ...prev, type: true }));
                    const error = validateField("type", projectData.type);
                    setErrors((prev) => ({
                      ...prev,
                      type: error,
                    }));
                  }}
                  className={`${
                    (submitAttempted || touched.type) && errors.type ? "editproject__input-error" : ""
                  }`}
                >
                  <option value="">Выберите тип проекта</option>
                  {projectTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                {(submitAttempted || touched.type) && errors.type && (
                  <span className="editproject__error-message">{errors.type}</span>
                )}
              </li>
              <li className="editproject__point">
                <span className="editproject__point-title">Тип здания</span>
                <select
                  name="buildingType"
                  value={projectData.buildingType}
                  onChange={handleInputChange}
                  onBlur={() =>
                    setTouched((prev) => ({ ...prev, buildingType: true }))
                  }
                  className={`${
                    touched.buildingType && errors.buildingType
                      ? "editproject__input-error"
                      : ""
                  }`}
                >
                  <option value="">Выберите тип здания</option>
                  <option value="МЖК">МЖК</option>
                  <option value="ИЖД">ИЖД</option>
                  <option value="ОЗ">ОЗ</option>
                </select>
                {(submitAttempted || touched.buildingType) && errors.buildingType && (
                  <span className="editproject__error-message">{errors.buildingType}</span>
                )}
              </li>
              <li className="editproject__point">
                <span className="editproject__point-title">Код проекта</span>
                <input
                  name="code"
                  value={projectData.code}
                  onChange={handleInputChange}
                  onBlur={() => setTouched((prev) => ({ ...prev, code: true }))}
                  className={`${
                    touched.code && errors.code ? "editproject__input-error" : ""
                  }`}
                  placeholder="Введите код проекта"
                />
                {(submitAttempted || touched.code) && errors.code && (
                  <span className="editproject__error-message">{errors.code}</span>
                )}
              </li>
              <li className="editproject__point">
                <span className="editproject__point-title">Вид проекта</span>
                <input
                  name="projectView"
                  value={projectData.projectView}
                  onChange={handleInputChange}
                  onBlur={() => {
                    setTouched((prev) => ({ ...prev, projectView: true }));
                    const error = validateField("projectView", projectData.projectView);
                    setErrors((prev) => ({
                      ...prev,
                      projectView: error,
                    }));
                  }}
                  className={`${
                    (submitAttempted || touched.projectView) && errors.projectView
                      ? "editproject__input-error"
                      : ""
                  }`}
                  placeholder="Введите Б или К(с номером)"
                />
                {(submitAttempted || touched.projectView) && errors.projectView && (
                  <span className="editproject__error-message">{errors.projectView}</span>
                )}
              </li>
              <li className="editproject__point">
                <span className="editproject__point-title">
                  Название проекта
                </span>
                <input
                  name="name"
                  value={projectData.name}
                  onChange={handleInputChange}
                  onBlur={() => {
                    setTouched((prev) => ({ ...prev, name: true }));
                    const error = validateField("name", projectData.name);
                    setErrors((prev) => ({
                      ...prev,
                      name: error,
                    }));
                  }}
                  className={`${
                    (submitAttempted || touched.name) && errors.name ? "editproject__input-error" : ""
                  }`}
                  placeholder="Введите название проекта"
                />
                {(submitAttempted || touched.name) && errors.name && (
                  <span className="editproject__error-message">{errors.name}</span>
                )}
              </li>
              <li className="editproject__point">
                <span className="editproject__point-title">Номер договора</span>
                <input
                  name="contractNumber"
                  value={projectData.contractNumber}
                  onChange={handleInputChange}
                  onBlur={() => {
                    setTouched((prev) => ({ ...prev, contractNumber: true }));
                    const error = validateField("contractNumber", projectData.contractNumber);
                    setErrors((prev) => ({
                      ...prev,
                      contractNumber: error,
                    }));
                  }}
                  className={`${
                    (submitAttempted || touched.contractNumber) && errors.contractNumber
                      ? "editproject__input-error"
                      : ""
                  }`}
                  placeholder="Введите номер договора"
                />
                {(submitAttempted || touched.contractNumber) && errors.contractNumber && (
                  <span className="editproject__error-message">{errors.contractNumber}</span>
                )}
              </li>
              <li className="editproject__point">
                <span className="editproject__point-title">Заказчик</span>
                <input
                  name="customer"
                  value={projectData.customer}
                  onChange={handleInputChange}
                  onBlur={() => {
                    setTouched((prev) => ({ ...prev, customer: true }));
                    const error = validateField("customer", projectData.customer);
                    setErrors((prev) => ({
                      ...prev,
                      customer: error,
                    }));
                  }}
                  className={`${
                    (submitAttempted || touched.customer) && errors.customer ? "editproject__input-error" : ""
                  }`}
                  placeholder="Введите заказчика"
                />
                {(submitAttempted || touched.customer) && errors.customer && (
                  <span className="editproject__error-message">{errors.customer}</span>
                )}
              </li>
              <li className="editproject__point">
                <span className="editproject__point-title">
                  Сумма по договору
                </span>
                <input
                  name="contractAmount"
                  value={projectData.contractAmount}
                  onChange={handleInputChange}
                  onBlur={() => {
                    setTouched((prev) => ({ ...prev, contractAmount: true }));
                    const error = validateField("contractAmount", projectData.contractAmount);
                    setErrors((prev) => ({
                      ...prev,
                      contractAmount: error,
                    }));
                  }}
                  className={`${
                    (submitAttempted || touched.contractAmount) && errors.contractAmount
                      ? "editproject__input-error"
                      : ""
                  }`}
                  placeholder="Введите сумму"
                />
                {(submitAttempted || touched.contractAmount) && errors.contractAmount && (
                  <span className="editproject__error-message">{errors.contractAmount}</span>
                )}
              </li>
              <li className="editproject__point">
                <span className="editproject__point-title">Статус</span>
                <select
                  name="status"
                  value={projectData.status}
                  onChange={handleInputChange}
                  onBlur={() => {
                    setTouched((prev) => ({ ...prev, status: true }));
                    const error = validateField("status", projectData.status);
                    setErrors((prev) => ({
                      ...prev,
                      status: error,
                    }));
                  }}
                  className={`${
                    (submitAttempted || touched.status) && errors.status ? "editproject__input-error" : ""
                  }`}
                >
                  <option value="">Выберите статус</option>
                  <option value="Перспективный">Перспективный</option>
                  <option value="Разработка">Разработка</option>
                  <option value="Экспертиза">Экспертиза</option>
                  <option value="Завершен">Завершен</option>
                  <option value="Авторский надзор">Авторский надзор</option>
                  <option value="Корректировка">Корректировка</option>
                  <option value="Приостановлен">Приостановлен</option>
                  <option value="Отказ">Отказ</option>
                </select>
                {(submitAttempted || touched.status) && errors.status && (
                  <span className="editproject__error-message">{errors.status}</span>
                )}
              </li>
              <li className="editproject__point">
                <span className="editproject__point-title">Ответственный</span>
                <Select
                  name="responsible"
                  options={users}
                  styles={customStyles(!!errors.responsible && (submitAttempted || touched.responsible))}
                  value={users.find(
                    (user) => user.value === projectData.responsible
                  )}
                  onChange={(selectedOption) =>
                    handleInputChange({
                      target: {
                        name: "responsible",
                        value: selectedOption.value,
                      },
                    })
                  }
                  onBlur={() => setTouched((prev) => ({ ...prev, responsible: true }))}
                  placeholder="Выберите ответственного"
                  noOptionsMessage={() => "Пользователи не найдены"}
                />
                {errors.responsible && (submitAttempted || touched.responsible) && (
                  <span className="editproject__error-message">{errors.responsible}</span>
                )}
              </li>
              <li className="editproject__point">
                <span className="editproject__point-title">Отделение</span>
                <Select
                  name="departmentId"
                  options={departments}
                  styles={customStyles(!!errors.departmentId && (submitAttempted || touched.departmentId))}
                  value={departments.find((dept) => dept.value === projectData.departmentId)}
                  onChange={(selectedOption) =>
                    handleInputChange({
                      target: {
                        name: "departmentId",
                        value: selectedOption ? selectedOption.value : "",
                      },
                    })
                  }
                  onBlur={() => setTouched((prev) => ({ ...prev, departmentId: true }))}
                  placeholder="Выберите отделение"
                  noOptionsMessage={() => "Отделения не найдены"}
                  isClearable
                />
                {errors.departmentId && (submitAttempted || touched.departmentId) && (
                  <span className="editproject__error-message">{errors.departmentId}</span>
                )}
              </li>
              <li className="editproject__point">
                <span className="editproject__point-title">
                  Дата подписания
                </span>
                <Calendar
                  value={
                    projectData.signingDate
                      ? new Date(projectData.signingDate)
                      : null
                  }
                  onChange={(date) =>
                    handleInputChange({
                      target: {
                        name: "signingDate",
                        value: date.toISOString().split("T")[0],
                      },
                    })
                  }
                  onBlur={() => {
                    setTouched((prev) => ({ ...prev, signingDate: true }));
                    const error = validateField("signingDate", projectData.signingDate);
                    setErrors((prev) => ({
                      ...prev,
                      signingDate: error,
                    }));
                  }}
                  className={`${
                    (submitAttempted || touched.signingDate) && errors.signingDate
                      ? "editproject__input-error"
                      : ""
                  }`}
                />
                {(submitAttempted || touched.signingDate) && errors.signingDate && (
                  <span className="editproject__error-message">{errors.signingDate}</span>
                )}
              </li>
              <li className="editproject__point">
                <span className="editproject__point-title">Дата начала</span>
                <div className={(submitAttempted || touched.startDate) && errors.startDate ? "editproject__calendar-error-wrapper" : ""}>
                  <Calendar
                    value={
                      projectData.startDate
                        ? new Date(projectData.startDate)
                        : null
                    }
                    onChange={(date) => {
                      handleInputChange({
                        target: {
                          name: "startDate",
                          value: date.toISOString().split("T")[0],
                        },
                      });
                      if (submitAttempted || touched.startDate) {
                        const error = validateField("startDate", date.toISOString().split("T")[0]);
                        setErrors((prev) => ({
                          ...prev,
                          startDate: error,
                        }));
                      }
                    }}
                    className={(submitAttempted || touched.startDate) && errors.startDate ? "editproject__input-error" : ""}
                  />
                </div>
                {(submitAttempted || touched.startDate) && errors.startDate && (
                  <span className="editproject__error-message">{errors.startDate}</span>
                )}
              </li>
              <li className="editproject__point">
                <span className="editproject__point-title">
                  Начало экспертизы
                </span>
                <div className={(submitAttempted || touched.expertiseStartDate) && errors.expertiseStartDate ? "editproject__calendar-error-wrapper" : ""}>
                  <Calendar
                    value={
                      projectData.expertiseStartDate
                        ? new Date(projectData.expertiseStartDate)
                        : null
                    }
                    onChange={(date) => {
                      handleInputChange({
                        target: {
                          name: "expertiseStartDate",
                          value: date.toISOString().split("T")[0],
                        },
                      });
                      if (submitAttempted || touched.expertiseStartDate) {
                        const error = validateField("expertiseStartDate", date.toISOString().split("T")[0]);
                        setErrors((prev) => ({
                          ...prev,
                          expertiseStartDate: error,
                        }));
                      }
                    }}
                    className={(submitAttempted || touched.expertiseStartDate) && errors.expertiseStartDate ? "editproject__input-error" : ""}
                  />
                </div>
                {(submitAttempted || touched.expertiseStartDate) && errors.expertiseStartDate && (
                  <span className="editproject__error-message">
                    {errors.expertiseStartDate}
                  </span>
                )}
              </li>
              <li className="editproject__point">
                <span className="editproject__point-title">
                  Конец экспертизы
                </span>
                <div className={(submitAttempted || touched.expertiseEndDate) && errors.expertiseEndDate ? "editproject__calendar-error-wrapper" : ""}>
                  <Calendar
                    value={
                      projectData.expertiseEndDate
                        ? new Date(projectData.expertiseEndDate)
                        : null
                    }
                    onChange={(date) => {
                      handleInputChange({
                        target: {
                          name: "expertiseEndDate",
                          value: date.toISOString().split("T")[0],
                        },
                      });
                      if (submitAttempted || touched.expertiseEndDate) {
                        const error = validateField("expertiseEndDate", date.toISOString().split("T")[0]);
                        setErrors((prev) => ({
                          ...prev,
                          expertiseEndDate: error,
                        }));
                      }
                    }}
                    className={(submitAttempted || touched.expertiseEndDate) && errors.expertiseEndDate ? "editproject__input-error" : ""}
                  />
                </div>
                {(submitAttempted || touched.expertiseEndDate) && errors.expertiseEndDate && (
                  <span className="editproject__error-message">
                    {errors.expertiseEndDate}
                  </span>
                )}
              </li>
              <li className="editproject__point">
                <span className="editproject__point-title">Дата окончания</span>
                <div className={(submitAttempted || touched.endDate) && errors.endDate ? "editproject__calendar-error-wrapper" : ""}>
                  <Calendar
                    value={
                      projectData.endDate ? new Date(projectData.endDate) : null
                    }
                    onChange={(date) => {
                      handleInputChange({
                        target: {
                          name: "endDate",
                          value: date.toISOString().split("T")[0],
                        },
                      });
                      if (submitAttempted || touched.endDate) {
                        const error = validateField("endDate", date.toISOString().split("T")[0]);
                        setErrors((prev) => ({
                          ...prev,
                          endDate: error,
                        }));
                      }
                    }}
                    className={(submitAttempted || touched.endDate) && errors.endDate ? "editproject__input-error" : ""}
                  />
                </div>
                {(submitAttempted || touched.endDate) && errors.endDate && (
                  <span className="editproject__error-message">{errors.endDate}</span>
                )}
              </li>
              <li className="editproject__point">
                <span className="editproject__point-title">Блоки проекта</span>
                <Select
                  isMulti
                  name="blocks"
                  options={[CREATE_BLOCK_OPTION, ...blocks]}
                  value={blocks.filter((block) =>
                    projectData.blocks.some(
                      (selectedBlock) => selectedBlock.label === block.label
                    )
                  )}
                  onChange={handleBlockChange}
                  onBlur={() => {
                    setTouched((prev) => ({ ...prev, blocks: true }));
                    const error = validateField("blocks", projectData.blocks);
                    setErrors((prev) => ({
                      ...prev,
                      blocks: error,
                    }));
                  }}
                  styles={multiSelectStyles(!!errors.blocks && (submitAttempted || touched.blocks))}
                  placeholder="Выберите блоки проекта"
                  noOptionsMessage={() => "Блоки не найдены"}
                />
                {(submitAttempted || touched.blocks) && errors.blocks && (
                  <span className="editproject__error-message">{errors.blocks}</span>
                )}
              </li>
              <FinansistPayrollMapping
                variant="editproject"
                value={finansistMapping}
                onChange={setFinansistMapping}
                selectStyles={customStyles}
              />
              <li className="editproject__point editproject__point--full-width">
                <span className="editproject__point-title">
                  Информация о проекте
                </span>
                <textarea
                  name="projectInfo"
                  value={projectData.projectInfo}
                  onChange={handleInputChange}
                  onBlur={() => {
                    setTouched((prev) => ({ ...prev, projectInfo: true }));
                    const error = validateField("projectInfo", projectData.projectInfo);
                    setErrors((prev) => ({
                      ...prev,
                      projectInfo: error,
                    }));
                  }}
                  className={`${
                    (submitAttempted || touched.projectInfo) && errors.projectInfo
                      ? "editproject__input-error"
                      : ""
                  }`}
                  placeholder="Введите информацию о проекте"
                  rows="4"
                />
                {(submitAttempted || touched.projectInfo) && errors.projectInfo && (
                  <span className="editproject__error-message">{errors.projectInfo}</span>
                )}
              </li>
            </>
          )}
        </ul>

        {((projectId && projectData) || (selectedProject && projectData)) && (
          <div className="editproject__buttons">
            <button
              type="submit"
              className={`editproject__button button-send ${
                !isFormValid() ? "button-disabled" : ""
              }`}
              disabled={!isFormValid()}
            >
              Сохранить
            </button>
            <button
              type="button"
              className="editproject__button button-reset"
              onClick={() => navigate("/projects/all")}
            >
              Отменить
            </button>
          </div>
        )}
      </form>

      {showPopup && (
        <div className="popup-overlay">
          <div className="popup">
            <div className="popup-icon"></div>
            <p className="popup-text">Проект успешно обновлен</p>
          </div>
        </div>
      )}

      <CreateBlockModal
        open={showCreateBlockModal}
        onClose={() => setShowCreateBlockModal(false)}
        existingBlocks={blocks}
        onCreate={handleCreateBlock}
        isSubmitting={createBlockSubmitting}
      />
    </div>
  );
};

export default EditProject;
